import React, { useState, useRef, useEffect } from "react"
import { useSocket } from "../contexts/SocketContext"
import { chatService } from "../services/apichat"
import VoiceRecorder from "./VioceRecorder"

const MessageInput = ({ conversationId, receiverId, onMessageSent }) => {
  const [message, setMessage] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)

  const { sendMessage, startTyping, stopTyping } = useSocket()
  const fileInputRef = useRef(null)
  const typingTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [])

  const handleSendMessage = async () => {
    if (!message.trim()) return

    try {
      const messageData = {
        receiverId,
        messageType: "text",
        text: message.trim()
      }

      // Only add conversationId if it exists (for existing conversations)
      if (conversationId) {
        messageData.conversationId = conversationId
      }

      sendMessage(messageData)

      setMessage("")
      if (conversationId) {
        stopTyping(conversationId)
      }
      setIsTyping(false)
      onMessageSent()
    } catch (error) {
      console.error("Error sending message:", error)
    }
  }

  const handleKeyPress = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleInputChange = e => {
    const value = e.target.value
    setMessage(value)

    // Typing indicators (only if conversation exists)
    if (value.trim() && !isTyping && conversationId) {
      setIsTyping(true)
      startTyping(conversationId)
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // Set new timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false)
      if (conversationId) {
        stopTyping(conversationId)
      }
    }, 1000)
  }

  const handleFileUpload = async e => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB")
      return
    }

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "audio/webm",
      "audio/mp3",
      "audio/wav",
      "audio/ogg"
    ]

    if (!allowedTypes.includes(file.type)) {
      alert(
        "File type not supported. Please upload images, documents, or audio files."
      )
      return
    }

    try {
      setUploadingFile(true)
      const response = await chatService.uploadFile(file)
      console.log("File upload response:", response)

      // Handle new API response structure: { success, status, message, data }
      if (response.success && response.data) {
        const messageData = {
          receiverId,
          messageType: "file",
          fileUrl: response.data.fileUrl,
          fileName: response.data.fileName,
          fileSize: response.data.fileSize,
          fileType: response.data.fileType
        }

        if (conversationId) {
          messageData.conversationId = conversationId
        }

        sendMessage(messageData)
        onMessageSent()
      } else {
        console.error("Unexpected file upload response:", response)
        alert("Failed to upload file. Please try again.")
      }
    } catch (error) {
      console.error("Error uploading file:", error)
      alert("Failed to upload file. Please try again.")
    } finally {
      setUploadingFile(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  const handleVoiceRecordingComplete = async (audioBlob, duration) => {
    try {
      console.log(
        "🎤 Voice recording completed, duration:",
        duration,
        "seconds"
      )
      console.log("🎤 Audio blob size:", audioBlob.size, "bytes")
      console.log("🎤 Audio blob type:", audioBlob.type)
      console.log("🎤 Duration received in MessageInput:", duration)

      setUploadingFile(true)

      // Create a file from the blob with proper naming
      const timestamp = Date.now()
      const audioFile = new File([audioBlob], `voice-${timestamp}.webm`, {
        type: audioBlob.type || "audio/webm"
      })

      console.log("🎤 Created audio file:", {
        name: audioFile.name,
        size: audioFile.size,
        type: audioFile.type
      })

      // Upload the voice file
      console.log("🎤 Uploading voice file...")
      const response = await chatService.uploadFile(audioFile)
      console.log("🎤 Voice upload response:", response)

      // Handle new API response structure: { success, status, message, data }
      if (response.success && response.data) {
        const messageData = {
          receiverId,
          messageType: "voice",
          voiceUrl: response.data.fileUrl,
          voiceDuration: duration // Use actual duration from recording
        }

        // Only add conversationId if it exists (for existing conversations)
        if (conversationId) {
          messageData.conversationId = conversationId
        }

        console.log("🎤 Sending voice message with data:", messageData)
        console.log("🎤 Voice duration being sent:", messageData.voiceDuration)
        sendMessage(messageData)
        onMessageSent()

        console.log("🎤 Voice message sent successfully")
      } else {
        console.error("❌ Unexpected voice upload response:", response)
        alert("Failed to send voice message. Please try again.")
      }
    } catch (error) {
      console.error("❌ Error uploading voice message:", error)
      console.error("❌ Error details:", {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      })
      alert("Failed to send voice message. Please try again.")
    } finally {
      setUploadingFile(false)
    }
  }

  return (
    <div
      className="w-full bg-[#212C43] border-t border-[#4A5567] px-2 sm:px-4 py-3"
    >
      <div
        className="flex items-center gap-2 sm:gap-3 py-2 flex-1"
      >
        {/* File Upload Button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingFile}
          className={`
            flex items-center justify-center w-10 h-10 rounded-full
            bg-[#f8f9fa] border border-[#e9ecef] text-[#6c757d] text-base shadow-sm
            transition-all duration-200 ease-in-out
            hover:bg-[#e9ecef] hover:scale-105
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
          title="Attach file"
        >
          📎
        </button>

        {/* Voice Recording Component */}
        <div className="flex items-center justify-center">
          <VoiceRecorder
            onRecordingComplete={handleVoiceRecordingComplete}
            disabled={uploadingFile}
          />
        </div>

        {/* Message Input */}
        <div className="flex-1 min-w-0">
          <textarea
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={uploadingFile}
            className="w-full min-h-[40px] max-h-[120px] px-4 py-2 border-2 border-[#4A5567] rounded-[25px] resize-none font-sans text-sm bg-[#212C43] text-white shadow-sm focus:shadow-md focus:outline-none disabled:opacity-50 transition-shadow duration-200"
          />
        </div>

        {/* Send Button */}
        <button
          onClick={handleSendMessage}
          disabled={!message.trim() || uploadingFile}
          className={`
            flex items-center justify-center w-10 h-10 rounded-full border-none p-0 shadow-sm
            transition-all duration-200 ease-in-out
            ${message.trim() ? 'bg-[#3662E3] cursor-pointer hover:scale-105 hover:shadow-md' : 'bg-[#cccccc] cursor-not-allowed scale-95'}
          `}
        >
          {uploadingFile ? (
            <div
              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
            ></div>
          ) : (
            <span
              className="text-white text-base transition-transform duration-200"
            >
              ➤
            </span>
          )}
        </button>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileUpload}
        className="hidden"
        accept="image/*,application/pdf,.doc,.docx,audio/*"
      />

      {/* Upload Progress */}
      {uploadingFile && (
        <div
          className="mt-2 text-xs text-gray-400 text-center"
        >
          Uploading...
        </div>
      )}
    </div>
  )
}

export default MessageInput

